import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ChartColumn,
  FileText,
  Inbox,
  LucideIcon,
  Megaphone,
  RefreshCw,
  Send,
  TriangleAlert,
  Users,
} from 'lucide-react-native';
import { Fragment, useEffect, useRef } from 'react';
import { Animated, Easing, Pressable, View } from 'react-native';

const kSteps: Array<{
  icon: LucideIcon;
  title: string;
  note: string;
  /** Drawn in the accent colour, as the design has it. */
  accent?: boolean;
}> = [
  { icon: Megaphone, title: '创建活动', note: '由店长或营销负责人发起' },
  { icon: Send, title: '触达客户', note: '通过门店微信逐一触达' },
  {
    icon: ChartColumn,
    title: '监控效果',
    note: '持续监控营销效果及时行动',
    accent: true,
  },
  { icon: FileText, title: '查看报告', note: '汇总并查看完整活动报告' },
];

/** Who the page serves, in rings around the reply tray. */
function Illustration() {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        width: rpx(128),
        height: rpx(128),
        borderRadius: rpx(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tint(aiColors.primary, 0.05),
      }}
    >
      <View
        style={{
          width: rpx(80),
          height: rpx(80),
          borderRadius: rpx(40),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tint(aiColors.primary, 0.1),
        }}
      >
        <View
          style={{
            width: rpx(56),
            height: rpx(56),
            borderRadius: rpx(16),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: aiColors.card,
            shadowColor: '#0F172A',
            shadowOpacity: 0.06,
            shadowRadius: 3,
            shadowOffset: { width: 0, height: 1 },
          }}
        >
          <Users size={28} color={aiColors.primary} strokeWidth={1.8} />
        </View>
      </View>
      <View
        style={{
          position: 'absolute',
          right: rpx(12),
          bottom: rpx(12),
          width: rpx(28),
          height: rpx(28),
          borderRadius: rpx(14),
          borderWidth: 2,
          borderColor: aiColors.background,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: aiColors.accent,
        }}
      >
        <Inbox size={14} color={aiColors.accentForeground} />
      </View>
    </View>
  );
}

/**
 * The page with no campaign to show: what a campaign does, step by step,
 * and the way to start one.
 */
export function DashboardEmpty({
  title,
  onCreate,
}: {
  title: string;
  onCreate: () => void;
}) {
  const { rpx } = useResponsiveContext();
  const width = { width: '100%', maxWidth: rpx(320) } as const;
  return (
    <View
      style={{
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: rpx(32),
      }}
    >
      <Illustration />
      <Typography.Text
        bold
        size={rpx(16)}
        color={aiColors.foreground}
        style={{ marginTop: rpx(24) }}
      >
        {title}
      </Typography.Text>
      <Typography.Text
        size={rpx(12)}
        lineHeight={rpx(19)}
        color={aiColors.mutedForeground}
        style={{ marginTop: rpx(8), maxWidth: rpx(320), textAlign: 'center' }}
      >
        使用私域营销工具唤回流失客户、提醒客户定期保养、推广促销活动并进行节日问候等。
      </Typography.Text>
      <View
        style={{
          ...width,
          marginTop: rpx(24),
          padding: rpx(16),
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          shadowColor: '#0F172A',
          shadowOpacity: 0.05,
          shadowRadius: 3,
          shadowOffset: { width: 0, height: 1 },
        }}
      >
        {kSteps.map((step, index) => (
          <Fragment key={step.title}>
            {index ? (
              <View
                style={{
                  marginLeft: rpx(14),
                  width: 1,
                  height: rpx(16),
                  backgroundColor: aiColors.border,
                }}
              />
            ) : null}
            <View style={{ flexDirection: 'row', gap: rpx(12) }}>
              <View
                style={{
                  width: rpx(28),
                  height: rpx(28),
                  borderRadius: rpx(8),
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: step.accent
                    ? aiColors.accent
                    : aiColors.secondary,
                }}
              >
                <step.icon
                  size={16}
                  color={
                    step.accent
                      ? aiColors.accentForeground
                      : aiColors.secondaryForeground
                  }
                />
              </View>
              <View style={{ flex: 1 }}>
                <Typography.Text
                  bold
                  size={rpx(12)}
                  color={aiColors.foreground}
                >
                  {step.title}
                </Typography.Text>
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.mutedForeground}
                >
                  {step.note}
                </Typography.Text>
              </View>
            </View>
          </Fragment>
        ))}
      </View>
      <Pressable
        accessibilityRole="button"
        onPress={onCreate}
        style={({ pressed }) => ({
          ...width,
          marginTop: rpx(24),
          alignItems: 'center',
          paddingVertical: rpx(10),
          borderRadius: rpx(12),
          backgroundColor: aiColors.primary,
          opacity: pressed ? 0.85 : 1,
        })}
      >
        <Typography.Text bold size={rpx(14)} color={aiColors.primaryForeground}>
          开始你的第一个营销活动！
        </Typography.Text>
      </Pressable>
    </View>
  );
}

export function DashboardLoading() {
  const { rpx } = useResponsiveContext();
  const turn = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(turn, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [turn]);
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: rpx(10),
      }}
    >
      <Animated.View
        style={{
          transform: [
            {
              rotate: turn.interpolate({
                inputRange: [0, 1],
                outputRange: ['0deg', '360deg'],
              }),
            },
          ],
        }}
      >
        <RefreshCw size={22} color={aiColors.mutedForeground} />
      </Animated.View>
      <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
        加载中...
      </Typography.Text>
    </View>
  );
}

export function DashboardFailed({ onRetry }: { onRetry: () => void }) {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: rpx(16),
      }}
    >
      <View
        style={{
          width: rpx(48),
          height: rpx(48),
          borderRadius: rpx(14),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tint(aiColors.destructive, 0.1),
        }}
      >
        <TriangleAlert size={22} color={aiColors.destructive} />
      </View>
      <Typography.Text bold size={rpx(15)} color={aiColors.foreground}>
        暂时无法加载，点击重试
      </Typography.Text>
      <Pressable
        accessibilityRole="button"
        onPress={onRetry}
        style={({ pressed }) => ({
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(8),
          borderRadius: rpx(10),
          backgroundColor: aiColors.secondary,
          opacity: pressed ? 0.85 : 1,
        })}
      >
        <Typography.Text
          bold
          size={rpx(13)}
          color={aiColors.secondaryForeground}
        >
          重试
        </Typography.Text>
      </Pressable>
    </View>
  );
}
